import {
    useGetPerformanceGraphQuery,
    useGetPerformanceReportExportMutation,
    useGetPerformanceReportQuery,
} from "api/TenantSlug/Report/hooks.ts";
import { TimeGraph } from "components/Graph/UI/TimeGraph/TimeGraph.tsx";
import { GeneralGraphUtils } from "components/Graph/utils/GeneralGraphUtils.ts";
import { ALLOWED_KEYS, DEFAULT_VISIBLE_KEYS } from "components/Report/common-config.ts";
import { ReportDateCell } from "components/Report/UI/ReportDateCell/ReportDateCell";
import { ReportSourceToggleButton } from "components/Report/UI/ReportSourceToggleButton/ReportSourceToggleButton.tsx";
import { StatusMultiSelect } from "components/Report/UI/StatusMultiSelect/StatusMultiSelect.tsx";
import { Table } from "components/UI/Table";
import { exportAtom } from "components/UI/Table/atoms/exportAtom.ts";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, tableAtom } from "components/UI/Table/atoms/tableAtom.ts";
import { FILTER_TYPES } from "components/UI/Table/constants/filter";
import { useTableDataApiOperations } from "components/UI/Table/hooks/useTableDataApiOperations";
import { TableColumnType } from "components/UI/Table/types/table.ts";
import { MoneyCell } from "components/UI/Table/UI/MoneyCell/MoneyCell.tsx";
import { NumberCell } from "components/UI/Table/UI/NumberCell/NumberCell.tsx";
import { PercentCell } from "components/UI/Table/UI/PercentCell/PercentCell.tsx";
import { useCreatedAtFilterValue } from "components/UI/Table/utils/getDefaultCreatedAtFilter";
import { generateSummary } from "components/UI/Table/utils/summary.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions.ts";
import { useReportDataSourceToggle } from "hooks/useReportDataSourceToggle.ts";
import { useUser } from "hooks/useUser.ts";
import { useEffect, Fragment } from "react";
import { useRecoilValue } from "recoil";
import {
    PayoutResult,
    PerformanceReportDataSourceEnum,
    PerformanceReportViewEnum,
    PermissionEnum,
} from "types/enums.ts";
import { TableId } from "types/models.ts";

import GetPerformanceReportResponse = App.Http.Responses.Report.GetPerformanceReportResponse;
import PerformanceReportSeriesResponse = App.Http.Responses.Report.PerformanceReportSeriesResponse;
import BaseExportRequest = App.Http.Requests.BaseExportRequest;

interface PerformanceReportProps {
    userId?: string;
    advertiserId?: string;
    offerId?: string;
}

export const PerformanceReport = (props: PerformanceReportProps) => {
    const userUtils = useUser();
    const tableId = TableId.PerformanceReportTable;
    const tableAtomInstance = tableAtom<PerformanceReportSeriesResponse>();
    const tableState = useRecoilValue(tableAtomInstance(tableId));
    const { t } = usePaldockTranslation("common");
    const { t: tR } = usePaldockTranslation("ui.report.columns");
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const permissionsUtils = usePermission();
    const createdAtStr = useCreatedAtFilterValue();
    const exportState = useRecoilValue(exportAtom(tableId));
    const { actualSource, actualStatuses, toggleSource, toggleStatuses } = useReportDataSourceToggle(
        PerformanceReportDataSourceEnum.ReportPayoutPerformances,
        [PayoutResult.Approved]
    );

    const isAdmin = userUtils.isAdmin();

    if (!isAdmin && !userUtils.isAffiliate()) {
        // TODO to refactor after BE multiroles refactor
        throw new Error("Reports for other roles than admin or affiliate not implemented.");
    }

    const defaultFilterKeys = ["datetime_at", "payout_result"];
    const tableFilterKeys = Object.keys(tableState.filter?.filter || {});
    const hasExtraFilters =
        tableFilterKeys.some((key) => !defaultFilterKeys.includes(key)) ||
        props.userId ||
        props.advertiserId ||
        props.offerId;

    const request = {
        page: {
            number: tableState.page?.number ?? DEFAULT_PAGE_NUMBER,
            size: tableState.page?.size ?? DEFAULT_PAGE_SIZE,
        },
        filter: {
            filter: {
                payout_result: {
                    in: actualStatuses,
                },
                ...(props.userId && { owner_id: props.userId }),
                ...(props.advertiserId && { advertiser_id: props.advertiserId }),
                ...(props.offerId && { offer_id: props.offerId }),
                ...(tableState.filter?.filter || {}),
                ...(tableState.filter?.filter?.datetime_at
                    ? {}
                    : {
                          datetime_at: {
                              btw: createdAtStr,
                          },
                      }),
            },
            sort: tableState.filter?.sort ?? "-datetime_at",
        },
        view: isAdmin ? PerformanceReportViewEnum.TenantAdmin : PerformanceReportViewEnum.AffiliatePartner,
        source: actualSource,
        ...(hasExtraFilters && { fill_timeline: 0 }),
    };

    const query = useGetPerformanceReportQuery(request, {
        enabled: false,
    });

    const {
        data: graphData,
        isLoading: graphLoading,
        isError: graphError,
    } = useGetPerformanceGraphQuery({
        filter: {
            filter: {
                payout_result: {
                    in: actualStatuses,
                },
                ...(props.userId && { owner_id: props.userId }),
                ...(props.advertiserId && { advertiser_id: props.advertiserId }),
                ...(props.offerId && { offer_id: props.offerId }),
                ...(tableState.filter?.filter || {}),
                ...(tableState.filter?.filter?.datetime_at
                    ? {}
                    : {
                          datetime_at: {
                              btw: createdAtStr,
                          },
                      }),
            },
        },
        view: isAdmin ? PerformanceReportViewEnum.TenantAdmin : PerformanceReportViewEnum.AffiliatePartner,
        source: actualSource,
    });

    const getPerformanceReportExportMutation = useGetPerformanceReportExportMutation();
    const tableFetchDataUtils = useTableDataApiOperations<
        GetPerformanceReportResponse,
        void,
        Record<string, never>,
        BaseExportRequest
    >(tableId, query, undefined, undefined, getPerformanceReportExportMutation);

    useEffect(() => {
        tableFetchDataUtils.setTableState((prev) => ({
            ...prev,
            page: { ...prev.page, number: 1 },
            filter: {
                filter: {
                    ...(prev.filter?.filter || {}),
                    payout_result: {
                        in: actualStatuses,
                    },
                },
            },
            source: actualSource,
        }));
    }, [actualStatuses, actualSource]);

    const columnConfig: TableColumnType<PerformanceReportSeriesResponse>[] = [
        {
            title: tG("date"),
            dataIndex: "datetime_at",
            ellipsis: true,
            key: "datetime_at",
            sorter: true,
            render: (dt: string) => <ReportDateCell<PerformanceReportSeriesResponse> value={dt} tableId={tableId} />,
            defaultSorter: true,
            defaultSortOrder: "descend",
            width: 150,
        },
        {
            title: tR("clicks"),
            dataIndex: "click",
            ellipsis: true,
            key: "click",
            render: (click) => <NumberCell amount={click} />,
            filterable: true,
            filterType: FILTER_TYPES.number,
            sorter: true,
            defaultSortOrder: "descend",
            width: 100,
        },
        {
            title: tR("leads"),
            dataIndex: "lead",
            ellipsis: true,
            key: "lead",
            render: (lead) => <NumberCell amount={lead} />,
            filterable: true,
            filterType: FILTER_TYPES.number,
            sorter: true,
            defaultSortOrder: "descend",
            width: 100,
        },
        {
            title: tR("transactions"),
            dataIndex: "count",
            ellipsis: true,
            key: "count",
            render: (count) => <NumberCell amount={count} />,
            filterable: true,
            filterType: FILTER_TYPES.number,
            sorter: true,
            defaultSortOrder: "descend",
            width: 100,
        },
        {
            title: tR("cpl"),
            dataIndex: "cpl",
            ellipsis: true,
            key: "cpl",
            render: (_, record) => <MoneyCell money={record.cpl} />,
            filterable: true,
            filterType: FILTER_TYPES.number,
            filterName: "cpl",
            sorter: true,
            defaultSortOrder: "descend",
            width: 100,
        },
        ...(isAdmin
            ? [
                  {
                      title: tR("cost"),
                      dataIndex: "cost",
                      ellipsis: true,
                      key: "cost",
                      render: (_: string, record: PerformanceReportSeriesResponse) => <MoneyCell money={record.cost} />,
                      filterable: true,
                      filterType: FILTER_TYPES.number,
                      filterName: "cost",
                      sorter: true,
                      defaultSortOrder: "descend" as const,
                      width: 130,
                  },
              ]
            : []),
        ...(isAdmin
            ? [
                  {
                      title: tR("epl"),
                      dataIndex: "epl",
                      ellipsis: true,
                      key: "epl",
                      render: (_: string, record: PerformanceReportSeriesResponse) => <MoneyCell money={record.epl} />,
                      filterable: true,
                      filterType: FILTER_TYPES.number,
                      filterName: "epl",
                      sorter: true,
                      defaultSortOrder: "descend" as const,
                      width: 130,
                  },
              ]
            : []),

        {
            title: tR("revenue"),
            dataIndex: "revenue",
            ellipsis: true,
            key: "revenue",
            render: (_, record) => <MoneyCell money={record.revenue} />,
            filterable: true,
            filterType: FILTER_TYPES.number,
            filterName: "revenue",
            sorter: true,
            defaultSortOrder: "descend",
            width: 130,
        },
        ...(isAdmin
            ? [
                  {
                      title: tR("profit"),
                      dataIndex: "profit",
                      ellipsis: true,
                      key: "profit",
                      render: (_: string, record: PerformanceReportSeriesResponse) => (
                          <MoneyCell money={record.profit} />
                      ),
                      filterName: "profit",
                      filterable: true,
                      filterType: FILTER_TYPES.number,
                      sorter: true,
                      defaultSortOrder: "descend" as const,
                      width: 130,
                  },
              ]
            : []),
        ...(isAdmin
            ? [
                  {
                      title: tR("roi"),
                      dataIndex: "roi",
                      ellipsis: true,
                      key: "roi",
                      render: (_: string, record: PerformanceReportSeriesResponse) => (
                          <PercentCell amount={record.roi} />
                      ),
                      filterable: true,
                      filterType: FILTER_TYPES.number,
                      sorter: true,
                      defaultSortOrder: "descend" as const,
                      width: 100,
                  },
              ]
            : []),
        ...(isAdmin
            ? [
                  {
                      title: tR("margin"),
                      dataIndex: "margin",
                      ellipsis: true,
                      key: "margin",
                      render: (_: string, record: PerformanceReportSeriesResponse) => (
                          <PercentCell amount={record.margin} />
                      ),
                      filterable: true,
                      filterType: FILTER_TYPES.number,
                      sorter: true,
                      defaultSortOrder: "descend" as const,
                      width: 100,
                  },
              ]
            : []),
    ];

    const extraFilters = [
        {
            dataIndex: "advertiser.nickname",
            title: tG("advertiser"),
            filterType: FILTER_TYPES.advertiserSelect,
            filterName: "advertiser_id",
        },
    ];

    const summaryKeys = isAdmin
        ? ["click", "lead", "count", "cpl", "cost", "epl", "revenue", "profit", "roi", "margin"]
        : ["click", "lead", "count", "cpl", "revenue"];

    const percentKeys = isAdmin ? ["roi", "margin"] : [];

    return (
        <Fragment>
            {permissionsUtils.hasPermission(PermissionEnum.ReportShowInGraph) && (
                <TimeGraph
                    graphId={"performance-graph"}
                    data={GeneralGraphUtils.prepareTimeData(graphData?.timeline)}
                    keys={ALLOWED_KEYS(isAdmin)}
                    defaultActiveKeys={DEFAULT_VISIBLE_KEYS(isAdmin)}
                    defaultVisibleKeys={DEFAULT_VISIBLE_KEYS(isAdmin)}
                    error={graphError}
                    loading={graphLoading}
                    summaryData={tableFetchDataUtils.tableData?.summary}
                />
            )}
            <div className={"mt-4"}>
                <Table<GetPerformanceReportResponse, PerformanceReportSeriesResponse>
                    tableId={tableId}
                    data={tableFetchDataUtils.tableData as GetPerformanceReportResponse}
                    dataSource={tableFetchDataUtils.tableData?.series || []}
                    columnConfig={columnConfig}
                    fixFirstColumn
                    enableFilters={permissionsUtils.hasPermission(PermissionEnum.ReportSelectFilter)}
                    enableTimeFilter={permissionsUtils.hasPermission(PermissionEnum.ReportSelectFilterTime)}
                    loading={tableFetchDataUtils.fetchQuery.isLoading || tableFetchDataUtils.fetchQuery.isFetching}
                    onRefresh={tableFetchDataUtils.refetchData}
                    showRefetchButton={permissionsUtils.hasPermission(PermissionEnum.ReportUpdate)}
                    extraFilters={extraFilters}
                    summary={(visibleColumns) =>
                        generateSummary(
                            tableFetchDataUtils.tableData?.summary,
                            summaryKeys,
                            percentKeys,
                            t("total"),
                            visibleColumns ?? undefined,
                            columnConfig,
                            false
                        )
                    }
                    autoScrollWidth={true}
                    dateFilterDataIndex={"datetime_at"}
                    onExport={() =>
                        tableFetchDataUtils.exportData({
                            extension: exportState.extension,
                            headers: exportState.headers,
                            export_all: exportState.range === "all",
                            page: request.page,
                            filter: request.filter,
                            delimiter: exportState.delimiter,
                        })
                    }
                    extraTopBarElements={
                        <StatusMultiSelect onChange={toggleStatuses} defaultValue={[PayoutResult.Approved]} />
                    }
                    extraTopBarIcons={[
                        <ReportSourceToggleButton
                            key="report-source-toggle"
                            actualSource={actualSource}
                            onToggle={toggleSource}
                        />,
                    ]}
                />
            </div>
        </Fragment>
    );
};
